import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DEFAULT_CALIBRATION_BAR_PX,
  DEFAULT_SCREEN_DPI,
  SCREEN_CALIBRATION_ENV_LOCAL_KEY,
  SCREEN_CALIBRATION_ENV_SESSION_KEY,
  SCREEN_CALIBRATION_LOCAL_KEY,
  SCREEN_CALIBRATION_SESSION_KEY,
  calculateCalibrationScale,
  defaultScreenCalibrationState,
  getCurrentCalibrationEnvironmentSnapshot,
  hasCalibrationEnvironmentChanged,
  parseScreenCalibrationEnvironmentSession,
  parseScreenCalibrationSession,
  resolveEffectiveScreenDpi,
  safeDetectScreenDpi,
  toPixelsPerMm,
  type ScreenCalibrationEnvironmentSnapshot,
  type ScreenCalibrationSessionState,
} from "../utils/screenCalibration";

const CALIBRATION_TOLERANCE_PERCENT = 5;

export function useEEGScreenCalibration() {
  const [autoScreenDpi, setAutoScreenDpi] =
    useState<number>(DEFAULT_SCREEN_DPI);
  const [screenCalibration, setScreenCalibration] =
    useState<ScreenCalibrationSessionState>(() => {
      if (typeof window === "undefined") {
        return defaultScreenCalibrationState();
      }
      try {
        return parseScreenCalibrationSession(
          window.localStorage.getItem(SCREEN_CALIBRATION_LOCAL_KEY) ??
            window.sessionStorage.getItem(SCREEN_CALIBRATION_SESSION_KEY),
        );
      } catch {
        return defaultScreenCalibrationState();
      }
    });
  const [calibrationEnvironmentBaseline, setCalibrationEnvironmentBaseline] =
    useState<ScreenCalibrationEnvironmentSnapshot | null>(() => {
      if (typeof window === "undefined") return null;
      try {
        return (
          parseScreenCalibrationEnvironmentSession(
            window.localStorage.getItem(SCREEN_CALIBRATION_ENV_LOCAL_KEY) ??
              window.sessionStorage.getItem(SCREEN_CALIBRATION_ENV_SESSION_KEY),
          ) || getCurrentCalibrationEnvironmentSnapshot()
        );
      } catch {
        return getCurrentCalibrationEnvironmentSnapshot();
      }
    });
  const [calibrationStale, setCalibrationStale] = useState(false);

  const effectiveScreenDpi = useMemo(
    () =>
      resolveEffectiveScreenDpi(
        autoScreenDpi,
        screenCalibration,
        DEFAULT_CALIBRATION_BAR_PX,
      ),
    [autoScreenDpi, screenCalibration],
  );
  const effectivePixelsPerMm = useMemo(
    () => toPixelsPerMm(effectiveScreenDpi),
    [effectiveScreenDpi],
  );
  const calibrationScaleMultiplier = useMemo(
    () => calculateCalibrationScale(effectiveScreenDpi, autoScreenDpi),
    [effectiveScreenDpi, autoScreenDpi],
  );
  const calibrationAutoPixelsPerMm = useMemo(
    () => toPixelsPerMm(autoScreenDpi),
    [autoScreenDpi],
  );
  const calibrationMeasuredVerticalPxPerMm = useMemo(() => {
    const mm = screenCalibration.verticalMm;
    if (typeof mm !== "number" || !Number.isFinite(mm) || mm <= 0) return null;
    return DEFAULT_CALIBRATION_BAR_PX / mm;
  }, [screenCalibration.verticalMm]);
  const calibrationMeasuredHorizontalPxPerMm = useMemo(() => {
    const mm = screenCalibration.horizontalMm;
    if (typeof mm !== "number" || !Number.isFinite(mm) || mm <= 0) return null;
    return DEFAULT_CALIBRATION_BAR_PX / mm;
  }, [screenCalibration.horizontalMm]);
  const calibrationVerticalDeltaPercent = useMemo(() => {
    if (!calibrationMeasuredVerticalPxPerMm || calibrationAutoPixelsPerMm <= 0)
      return null;
    return (
      (Math.abs(
        calibrationMeasuredVerticalPxPerMm - calibrationAutoPixelsPerMm,
      ) /
        calibrationAutoPixelsPerMm) *
      100
    );
  }, [calibrationAutoPixelsPerMm, calibrationMeasuredVerticalPxPerMm]);
  const calibrationHorizontalDeltaPercent = useMemo(() => {
    if (
      !calibrationMeasuredHorizontalPxPerMm ||
      calibrationAutoPixelsPerMm <= 0
    )
      return null;
    return (
      (Math.abs(
        calibrationMeasuredHorizontalPxPerMm - calibrationAutoPixelsPerMm,
      ) /
        calibrationAutoPixelsPerMm) *
      100
    );
  }, [calibrationAutoPixelsPerMm, calibrationMeasuredHorizontalPxPerMm]);
  const calibrationVerticalPass = useMemo(() => {
    if (calibrationVerticalDeltaPercent === null) return null;
    return calibrationVerticalDeltaPercent <= CALIBRATION_TOLERANCE_PERCENT;
  }, [calibrationVerticalDeltaPercent]);
  const calibrationHorizontalPass = useMemo(() => {
    if (calibrationHorizontalDeltaPercent === null) return null;
    return calibrationHorizontalDeltaPercent <= CALIBRATION_TOLERANCE_PERCENT;
  }, [calibrationHorizontalDeltaPercent]);
  const calibrationOverallPass = useMemo(() => {
    const checks = [calibrationVerticalPass, calibrationHorizontalPass].filter(
      (value): value is boolean => value !== null,
    );
    if (!checks.length) return null;
    return checks.every(Boolean);
  }, [calibrationHorizontalPass, calibrationVerticalPass]);
  const calibrationStatus: "auto" | "manual" | "stale" = useMemo(() => {
    if (calibrationStale) return "stale";
    return screenCalibration.manualOverrideEnabled ? "manual" : "auto";
  }, [calibrationStale, screenCalibration.manualOverrideEnabled]);
  const calibrationBadgeLabel = useMemo(() => {
    if (calibrationStatus === "stale") return "Stale";
    if (calibrationStatus === "manual") return "Manual";
    return "Auto";
  }, [calibrationStatus]);
  const calibrationBadgeTitle = useMemo(() => {
    const statusPrefix =
      calibrationStatus === "stale"
        ? "Calibration stale due to screen/zoom change"
        : calibrationStatus === "manual"
          ? "Manual screen calibration active"
          : "Automatic screen calibration active";
    return `${statusPrefix} (${effectiveScreenDpi.toFixed(1)} DPI, ${effectivePixelsPerMm.toFixed(2)} px/mm)`;
  }, [calibrationStatus, effectivePixelsPerMm, effectiveScreenDpi]);

  const markCalibrationReviewed = useCallback(() => {
    const snapshot = getCurrentCalibrationEnvironmentSnapshot();
    if (!snapshot) return;
    setCalibrationEnvironmentBaseline(snapshot);
    setCalibrationStale(false);
  }, []);

  const autoScreenDpiRef = useRef<number>(autoScreenDpi);
  useEffect(() => {
    autoScreenDpiRef.current = autoScreenDpi;
  }, [autoScreenDpi]);

  const calibrationEnvironmentBaselineRef =
    useRef<ScreenCalibrationEnvironmentSnapshot | null>(
      calibrationEnvironmentBaseline,
    );
  useEffect(() => {
    calibrationEnvironmentBaselineRef.current = calibrationEnvironmentBaseline;
  }, [calibrationEnvironmentBaseline]);

  const lastObservedCalibrationEnvironmentRef =
    useRef<ScreenCalibrationEnvironmentSnapshot | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const syncAutoScreenDpi = () => {
      const detectedDpi = safeDetectScreenDpi();
      if (detectedDpi === autoScreenDpiRef.current) return;
      setAutoScreenDpi(detectedDpi);
    };

    const evaluateCalibrationEnvironment = (
      forceDpiRefresh: boolean = false,
    ) => {
      const current = getCurrentCalibrationEnvironmentSnapshot();
      if (!current) return;

      const previous = lastObservedCalibrationEnvironmentRef.current;
      const environmentChanged =
        forceDpiRefresh ||
        !previous ||
        hasCalibrationEnvironmentChanged(previous, current);
      lastObservedCalibrationEnvironmentRef.current = current;

      if (environmentChanged) {
        syncAutoScreenDpi();
      }

      const baseline = calibrationEnvironmentBaselineRef.current;
      if (!baseline) return;
      const stale = hasCalibrationEnvironmentChanged(baseline, current);
      setCalibrationStale((prev) => (prev === stale ? prev : stale));
    };

    evaluateCalibrationEnvironment(true);

    const onResize = () => {
      evaluateCalibrationEnvironment(true);
    };
    window.addEventListener("resize", onResize);
    const intervalId = window.setInterval(() => {
      evaluateCalibrationEnvironment(false);
    }, 1000);
    return () => {
      window.removeEventListener("resize", onResize);
      window.clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(
        SCREEN_CALIBRATION_LOCAL_KEY,
        JSON.stringify(screenCalibration),
      );
      window.sessionStorage.setItem(
        SCREEN_CALIBRATION_SESSION_KEY,
        JSON.stringify(screenCalibration),
      );
    } catch {
      // Ignore storage failures (private mode / quota)
    }
  }, [screenCalibration]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (calibrationEnvironmentBaseline) {
        window.localStorage.setItem(
          SCREEN_CALIBRATION_ENV_LOCAL_KEY,
          JSON.stringify(calibrationEnvironmentBaseline),
        );
        window.sessionStorage.setItem(
          SCREEN_CALIBRATION_ENV_SESSION_KEY,
          JSON.stringify(calibrationEnvironmentBaseline),
        );
      }
    } catch {
      // Ignore storage failures (private mode / quota)
    }
  }, [calibrationEnvironmentBaseline]);

  return {
    autoScreenDpi,
    setAutoScreenDpi,
    screenCalibration,
    setScreenCalibration,
    calibrationEnvironmentBaseline,
    calibrationStale,
    effectiveScreenDpi,
    effectivePixelsPerMm,
    calibrationScaleMultiplier,
    calibrationAutoPixelsPerMm,
    calibrationMeasuredVerticalPxPerMm,
    calibrationMeasuredHorizontalPxPerMm,
    calibrationVerticalDeltaPercent,
    calibrationHorizontalDeltaPercent,
    calibrationVerticalPass,
    calibrationHorizontalPass,
    calibrationOverallPass,
    calibrationStatus,
    calibrationBadgeLabel,
    calibrationBadgeTitle,
    markCalibrationReviewed,
  };
}
